import useCommonApiErrorHandling from '@/hooks/useCommonApiErrorHandling';
import useInsertServiceIdInApiUrl from '@/hooks/useInsertServiceIdInApiUrl';
import useSetLoading from '@/hooks/useSetLoading';
import { documentState } from '@/mobile/states/documentState';
import { DocumentResponse } from '@/mobile/types/api/mypage/document';
import apiMobile from '@/mobile/utils/apiMobile';
import { ApiUrlMobile } from '@/mobile/utils/constants';
import { getSigV4HeadersMobileForGet } from '@/utils/cognito';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';

const apiUrl = ApiUrlMobile.Read.DOCUMENT;

/**
 * Documentx取得用カスタムフック
 * API呼び出し・ローディング制御・エラーハンドリングを共通化し、
 * 呼び出し元では execute() を呼ぶだけで取得処理が完結する。
 *
 * @returns execute - API取得処理を実行する関数
 */
const useGetDocument = () => {
  const { startLoading, finishLoading } = useSetLoading();

  const setDocumentState = useSetRecoilState(documentState);

  const { insertServiceIdInApiUrl } = useInsertServiceIdInApiUrl();
  const { commonErrorHandling, commonResponseCodeHandling } = useCommonApiErrorHandling();

  const execute = useCallback(async (display: string | null = null) => {
    // 取得対象を絞り込めるようにしておくことで、画面ごとに必要な帳票だけを同じ hook で扱えるようにする。
    const queryParams = new URLSearchParams();
    if (display) {
      queryParams.append('display', display);
    }
    let apiUrlWithServiceId = insertServiceIdInApiUrl(apiUrl);
    if ([...queryParams].length > 0) {
      apiUrlWithServiceId = `${apiUrlWithServiceId}?${queryParams.toString()}`;
    }
    const headers = await getSigV4HeadersMobileForGet(apiUrlWithServiceId);

    startLoading(apiUrlWithServiceId);
    await apiMobile
      .get(apiUrlWithServiceId, headers)
      .then(({ data }: AxiosResponse<DocumentResponse>) => {
        const { isError } = commonResponseCodeHandling({
          responseCode: data.response_code,
          isTransitionTop: true,
        });

        if (isError) return;

        // 帳票一覧はそのまま UI に反映するため、ここでは加工せず state に保持して描画側へ委ねる。
        setDocumentState(data);
      })
      .catch((error: any) => {
        commonErrorHandling({ error, isTransitionTop: true });
      })
      .finally(() => finishLoading(apiUrlWithServiceId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { execute };
};

export default useGetDocument;
